import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Badge,
  Button,
  Form,
  Modal,
} from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vacationApplicationsApiService } from '../../services/apiServices';
import { VacationApplicationResponse } from '../../api-client';
import {
  getVacationApplicationStatusLabel,
  getVacationApplicationStatusBadgeVariant,
  getVacationApplicationTypeLabel,
  getVacationApplicationTypeBadgeVariant,
  vacationStatusAtLeast,
} from '../utils/statusConverters';
import { VacationApplicationStatus } from '../../api-client';
import 'bootstrap-daterangepicker/daterangepicker.css';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { Moment } from 'moment';

const MyVacationDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<VacationApplicationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [downloadingOrder, setDownloadingOrder] = useState(false);
  const [downloadingSignedOrder, setDownloadingSignedOrder] = useState(false);
  const [orderFileError, setOrderFileError] = useState<string | null>(null);

  const [signedOrderFile, setSignedOrderFile] = useState<File | null>(null);
  const [uploadingSignedOrder, setUploadingSignedOrder] = useState(false);
  const [uploadSignedOrderError, setUploadSignedOrderError] = useState<string | null>(null);
  const [uploadSignedOrderSuccess, setUploadSignedOrderSuccess] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [rescheduleFile, setRescheduleFile] = useState<File | null>(null);
  const [rescheduleStartDate, setRescheduleStartDate] = useState('');
  const [rescheduleEndDate, setRescheduleEndDate] = useState('');
  const [rescheduleDateLabel, setRescheduleDateLabel] = useState('Выберите период');
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadingReschedule, setUploadingReschedule] = useState(false);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [rescheduleSuccess, setRescheduleSuccess] = useState(false);

  useEffect(() => {
    if (!applicationId) {
      navigate('/vacation/my');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await vacationApplicationsApiService.getMyApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const reloadApplication = async () => {
    if (!applicationId) return;
    try {
      const response = await vacationApplicationsApiService.getMyApplication(applicationId);
      setApplication(response.data || null);
    } catch {}
  };

  const handleDownloadFile = async () => {
    if (!application || !applicationId) return;

    try {
      setDownloadingFile(true);
      setFileError(null);

      const response = await vacationApplicationsApiService.getMyApplicationFileUrl(applicationId);
      const fileUrl = response.data?.file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на файл');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setFileError(err.message || 'Ошибка при скачивании файла');
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleDownloadOrder = async () => {
    if (!applicationId) return;

    try {
      setDownloadingOrder(true);
      setOrderFileError(null);

      const response = await vacationApplicationsApiService.getMyOrderUrl(applicationId);
      const fileUrl = response.data?.file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на приказ');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setOrderFileError(err.message || 'Ошибка при скачивании приказа');
    } finally {
      setDownloadingOrder(false);
    }
  };

  const handleDownloadSignedOrder = async () => {
    if (!applicationId) return;

    try {
      setDownloadingSignedOrder(true);
      setOrderFileError(null);

      const response = await vacationApplicationsApiService.getMySignedOrderUrl(applicationId);
      const fileUrl = response.data?.file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на подписанный приказ');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setOrderFileError(err.message || 'Ошибка при скачивании подписанного приказа');
    } finally {
      setDownloadingSignedOrder(false);
    }
  };

  const handleUploadSignedOrder = async () => {
    if (!applicationId || !signedOrderFile) return;

    try {
      setUploadingSignedOrder(true);
      setUploadSignedOrderError(null);
      setUploadSignedOrderSuccess(false);

      const tempFileResponse = await vacationApplicationsApiService.uploadTempFile(signedOrderFile);
      const fileUrl = tempFileResponse.data?.file_url;
      if (!fileUrl) {
        throw new Error('Не удалось загрузить файл');
      }

      await vacationApplicationsApiService.uploadMySignedOrder(
        applicationId,
        fileUrl,
        signedOrderFile.name
      );

      const updatedResponse = await vacationApplicationsApiService.getMyApplication(applicationId);
      setApplication(updatedResponse.data || null);
      setUploadSignedOrderSuccess(true);
      setSignedOrderFile(null);
    } catch (err: any) {
      setUploadSignedOrderError(err.message || 'Ошибка при загрузке подписанного приказа');
    } finally {
      setUploadingSignedOrder(false);
    }
  };

  const handleConfirm = async () => {
    if (!applicationId) return;

    try {
      setActionLoading(true);
      setActionError(null);
      await vacationApplicationsApiService.confirmMyVacation(applicationId);
      await reloadApplication();
      setShowConfirmModal(false);
    } catch (err: any) {
      setActionError(err.message || 'Ошибка при подтверждении отпуска');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!applicationId) return;

    try {
      setActionLoading(true);
      setActionError(null);
      await vacationApplicationsApiService.cancelMyVacation(applicationId);
      await reloadApplication();
      setShowCancelModal(false);
    } catch (err: any) {
      setActionError(err.message || 'Ошибка при отмене отпуска');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!application) return;
    try {
      setDownloadingTemplate(true);
      const response = await vacationApplicationsApiService.getTemplate(application.type);
      const fileUrl = response.data?.file_url;
      if (fileUrl) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err: any) {
      setRescheduleError(err.message || 'Ошибка при загрузке шаблона');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleRescheduleDateRange = (start: Moment, end: Moment) => {
    setRescheduleStartDate(start.format('YYYY-MM-DD'));
    setRescheduleEndDate(end.format('YYYY-MM-DD'));
    setRescheduleDateLabel(`${start.format('DD.MM.YYYY')} - ${end.format('DD.MM.YYYY')}`);
  };

  const handleRescheduleSubmit = async () => {
    if (!applicationId || !rescheduleFile || !rescheduleStartDate || !rescheduleEndDate) return;

    try {
      setUploadingReschedule(true);
      setRescheduleError(null);

      const tempFileResponse = await vacationApplicationsApiService.uploadTempFile(rescheduleFile);
      const fileUrl = tempFileResponse.data?.file_url;
      if (!fileUrl) {
        throw new Error('Не удалось загрузить файл');
      }

      await vacationApplicationsApiService.rescheduleMyVacation(
        applicationId,
        rescheduleStartDate,
        rescheduleEndDate,
        fileUrl,
        rescheduleFile.name
      );

      await reloadApplication();
      setRescheduleSuccess(true);
      setShowRescheduleForm(false);
      setRescheduleFile(null);
      setRescheduleStartDate('');
      setRescheduleEndDate('');
      setRescheduleDateLabel('Выберите период');
    } catch (err: any) {
      setRescheduleError(err.message || 'Ошибка при переносе отпуска');
    } finally {
      setUploadingReschedule(false);
    }
  };

  const isOrderSigned = application?.status === VacationApplicationStatus.ORDER_SIGNED;

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/vacation/my')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявка на отпуск</h2>
            <Link to="/vacation/my">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          {fileError && <Alert variant="warning">{fileError}</Alert>}
          {orderFileError && <Alert variant="warning">{orderFileError}</Alert>}
          {uploadSignedOrderError && <Alert variant="danger">{uploadSignedOrderError}</Alert>}
          {uploadSignedOrderSuccess && (
            <Alert variant="success">Подписанный приказ успешно загружен</Alert>
          )}
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          {rescheduleError && <Alert variant="danger">{rescheduleError}</Alert>}
          {rescheduleSuccess && <Alert variant="success">Отпуск успешно перенесён</Alert>}

          <Card>
            <Card.Header>
              <Badge bg={getVacationApplicationStatusBadgeVariant(application.status)}>
                {getVacationApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>ID заявки</th>
                    <td>{application.id}</td>
                  </tr>
                  <tr>
                    <th>Тип отпуска</th>
                    <td>
                      <Badge bg={getVacationApplicationTypeBadgeVariant(application.type)}>
                        {getVacationApplicationTypeLabel(application.type)}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Статус</th>
                    <td>
                      <Badge bg={getVacationApplicationStatusBadgeVariant(application.status)}>
                        {getVacationApplicationStatusLabel(application.status)}
                      </Badge>
                    </td>
                  </tr>
                  {application.start_date && (
                    <tr>
                      <th>Дата начала</th>
                      <td>{application.start_date}</td>
                    </tr>
                  )}
                  {application.end_date && (
                    <tr>
                      <th>Дата окончания</th>
                      <td>{application.end_date}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Заявление</th>
                    <td>
                      <Button
                        variant="primary"
                        onClick={handleDownloadFile}
                        disabled={downloadingFile}
                        size="sm"
                      >
                        {downloadingFile ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Загрузка...
                          </>
                        ) : (
                          'Скачать заявление'
                        )}
                      </Button>
                    </td>
                  </tr>
                  {vacationStatusAtLeast(
                    application.status,
                    VacationApplicationStatus.ORDER_SIGNING
                  ) && (
                    <tr>
                      <th>Приказ</th>
                      <td>
                        <Button
                          variant="outline-primary"
                          onClick={handleDownloadOrder}
                          disabled={downloadingOrder}
                          size="sm"
                        >
                          {downloadingOrder ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Загрузка...
                            </>
                          ) : (
                            'Скачать приказ'
                          )}
                        </Button>
                      </td>
                    </tr>
                  )}
                  {vacationStatusAtLeast(
                    application.status,
                    VacationApplicationStatus.ORDER_SIGNED
                  ) && (
                    <tr>
                      <th>Подписанный приказ</th>
                      <td>
                        <Button
                          variant="outline-primary"
                          onClick={handleDownloadSignedOrder}
                          disabled={downloadingSignedOrder}
                          size="sm"
                        >
                          {downloadingSignedOrder ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Загрузка...
                            </>
                          ) : (
                            'Скачать подписанный приказ'
                          )}
                        </Button>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <th>Дата создания</th>
                    <td>{new Date(application.created_at).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>

              {(application.status === VacationApplicationStatus.ORDER_SIGNING ||
                application.status === VacationApplicationStatus.ORDER_SIGNED) && (
                <>
                  <hr />
                  <h5>Загрузка подписанного приказа</h5>
                  {application.status === VacationApplicationStatus.ORDER_SIGNED && (
                    <Alert variant="warning" className="py-2 px-3 small mb-2">
                      У заявки уже есть подписанный приказ. При загрузке нового подписанного приказа
                      существующий будет заменён.
                    </Alert>
                  )}
                  <p className="text-muted small">
                    Загрузите подписанный вами приказ, чтобы завершить процесс
                  </p>
                  <Form.Group>
                    <Form.Control
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const target = e.target as HTMLInputElement;
                        setSignedOrderFile(target.files?.[0] || null);
                      }}
                      disabled={uploadingSignedOrder}
                      size="sm"
                    />
                  </Form.Group>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-2"
                    onClick={handleUploadSignedOrder}
                    disabled={uploadingSignedOrder || !signedOrderFile}
                  >
                    {uploadingSignedOrder ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Загрузка...
                      </>
                    ) : (
                      'Загрузить подписанный приказ'
                    )}
                  </Button>
                </>
              )}

              {isOrderSigned && (
                <>
                  <hr />
                  <h5>Решение по отпуску</h5>
                  <p className="text-muted small">
                    Приказ подписан. Примите решение: пойти в отпуск, перенести или отменить.
                  </p>
                  <div className="d-flex gap-2">
                    <Button variant="success" size="sm" onClick={() => setShowConfirmModal(true)}>
                      Пойду в отпуск
                    </Button>
                    <Button variant="warning" size="sm" onClick={() => setShowRescheduleForm(true)}>
                      Перенести
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => setShowCancelModal(true)}>
                      Отменить
                    </Button>
                  </div>
                </>
              )}

              {isOrderSigned && showRescheduleForm && (
                <>
                  <hr />
                  <h5>Перенос отпуска</h5>
                  <div className="mb-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDownloadTemplate}
                      disabled={downloadingTemplate}
                    >
                      {downloadingTemplate ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Загрузка...
                        </>
                      ) : (
                        'Скачать шаблон заявления'
                      )}
                    </Button>
                    <Form.Text className="text-muted d-block">
                      Скачайте шаблон, заполните и подпишите, затем загрузите ниже
                    </Form.Text>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label>Новые даты отпуска *</Form.Label>
                    <DateRangePicker
                      initialSettings={{
                        locale: {
                          format: 'DD.MM.YYYY',
                          separator: ' - ',
                          applyLabel: 'Применить',
                          cancelLabel: 'Отмена',
                          fromLabel: 'от',
                          toLabel: 'до',
                          customRangeLabel: 'Выбрать диапазон',
                          weekLabel: 'Н',
                          daysOfWeek: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
                          monthNames: [
                            'Январь',
                            'Февраль',
                            'Март',
                            'Апрель',
                            'Май',
                            'Июнь',
                            'Июль',
                            'Август',
                            'Сентябрь',
                            'Октябрь',
                            'Ноябрь',
                            'Декабрь',
                          ],
                          firstDay: 1,
                        },
                      }}
                      onCallback={handleRescheduleDateRange}
                    >
                      <Form.Control
                        type="text"
                        value={rescheduleDateLabel}
                        readOnly
                        style={{ cursor: 'pointer' }}
                      />
                    </DateRangePicker>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Заявление (подписанное)</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const target = e.target as HTMLInputElement;
                        setRescheduleFile(target.files?.[0] || null);
                      }}
                      disabled={uploadingReschedule}
                      size="sm"
                    />
                  </Form.Group>
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleRescheduleSubmit}
                      disabled={
                        uploadingReschedule ||
                        !rescheduleFile ||
                        !rescheduleStartDate ||
                        !rescheduleEndDate
                      }
                    >
                      {uploadingReschedule ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Отправка...
                        </>
                      ) : (
                        'Отправить'
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setShowRescheduleForm(false);
                        setRescheduleFile(null);
                        setRescheduleStartDate('');
                        setRescheduleEndDate('');
                        setRescheduleDateLabel('Выберите период');
                        setRescheduleError(null);
                      }}
                      disabled={uploadingReschedule}
                    >
                      Отмена
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Подтверждение отпуска</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите подтвердить отпуск? Это действие нельзя будет отменить.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
            disabled={actionLoading}
          >
            Отмена
          </Button>
          <Button variant="success" onClick={handleConfirm} disabled={actionLoading}>
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Отправка...
              </>
            ) : (
              'Подтвердить'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Отмена отпуска</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите отменить отпуск? Это действие нельзя будет отменить.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            disabled={actionLoading}
          >
            Назад
          </Button>
          <Button variant="danger" onClick={handleCancel} disabled={actionLoading}>
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Отправка...
              </>
            ) : (
              'Отменить отпуск'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MyVacationDetailPage;
